import { memo, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import {
  ChartCard,
  ChartEmptyState,
  ChartErrorState,
  ChartLoading,
  MultiLineChart,
  type MultiLineChartConfig,
  type MultiLineChartPin,
  type MultiLineChartThreshold,
} from "@/shared/charts";
import { TrendingUpIcon } from "@/shared/ui/console-icons";
import type { ECharts } from "echarts";
import type { TotalPopulationTrendUnconfiguredArea } from "@situate/contracts/runtime";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics/types";
import {
  AVERAGE_TOTAL_POPULATION_SERIES_ID,
  AVERAGE_DUTY_FREE_POPULATION_SERIES_ID,
  PEAK_DUTY_FREE_POPULATION_SERIES_ID,
  PEAK_TOTAL_POPULATION_SERIES_ID,
  getPopulationTrendAnalyticsErrorMessage,
  mapPopulationTrendAnalyticsToMultiLineChartData,
  type PopulationTrendMetric,
} from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics/population-trend-analytics-chart.utils";

interface PopulationTrendAnalyticsChartProps {
  data: PopulationTrendAnalyticsPoint[];
  loading?: boolean;
  waitingForFilters?: boolean;
  error?: Error | string | null;
  requestedAreaIds?: readonly string[];
  configuredAreaIds?: readonly string[];
  unconfiguredAreas?: readonly TotalPopulationTrendUnconfiguredArea[];
  onChartReady?: (instance: ECharts) => void;
  /**
   * Horizontal capacity/threshold reference lines for the selected area.
   * Only pass these when exactly one area is selected; the Total Population
   * calculation itself never sums or derives thresholds across areas.
   */
  thresholds?: MultiLineChartThreshold[];
  xAxisRange?: { min: number; max: number };
  playbackTimestamp?: number;
  additionalPins?: readonly MultiLineChartPin[];
  metric?: PopulationTrendMetric;
}

function createChartConfig(
  thresholds: MultiLineChartThreshold[] | undefined,
  thresholdsVisible: boolean,
  xAxisRange: { min: number; max: number } | undefined,
  metric: PopulationTrendMetric
): MultiLineChartConfig {
  const isDutyFree = metric === "duty-free";
  const metricLabel = isDutyFree ? "Duty Free Population" : "Total Population";
  const averageSeriesId = isDutyFree
    ? AVERAGE_DUTY_FREE_POPULATION_SERIES_ID
    : AVERAGE_TOTAL_POPULATION_SERIES_ID;
  const peakSeriesId = isDutyFree
    ? PEAK_DUTY_FREE_POPULATION_SERIES_ID
    : PEAK_TOTAL_POPULATION_SERIES_ID;
  return {
    series: [
      {
        id: averageSeriesId,
        label: `Average ${metricLabel}`,
        type: "bar",
        thresholdColoring: "severity",
      },
      {
        id: peakSeriesId,
        label: `Peak ${metricLabel}`,
        thresholdColoring: "severity",
      },
    ],
    ariaDescription: `${metricLabel} trend chart showing average and peak population over time`,
    xAxisRange,
    thresholds,
    thresholdsVisible,
    peakLine: {
      seriesId: peakSeriesId,
      formatLabel: (peakValue) => `Peak: ${Math.round(peakValue).toLocaleString("en-GB")}`,
    },
    ...(thresholds == null ? {} : { thresholds }),
  };
}

export const PopulationTrendAnalyticsChart = memo(function PopulationTrendAnalyticsChart({
  data,
  loading = false,
  waitingForFilters = false,
  error = null,
  requestedAreaIds = [],
  configuredAreaIds = [],
  unconfiguredAreas = [],
  onChartReady,
  thresholds,
  xAxisRange,
  playbackTimestamp,
  additionalPins,
  metric = "total",
}: PopulationTrendAnalyticsChartProps) {
  const [showThresholds, setShowThresholds] = useState(true);
  const chartData = useMemo(
    () => mapPopulationTrendAnalyticsToMultiLineChartData(data, metric),
    [data, metric]
  );
  const chartConfig = useMemo(
    () => createChartConfig(thresholds, showThresholds, xAxisRange, metric),
    [thresholds, showThresholds, xAxisRange, metric]
  );
  const metricLabel = metric === "duty-free" ? "Duty Free Population" : "Total Population";
  const errorMessage = getPopulationTrendAnalyticsErrorMessage(error);
  const excludedAreaNames = unconfiguredAreas.map((area) => area.area_name).join(", ");
  const selectedAreaCount = requestedAreaIds.length;
  const configuredAreaCount = configuredAreaIds.length;
  const isAllUnconfigured =
    metric === "total" &&
    selectedAreaCount > 0 &&
    configuredAreaCount === 0 &&
    unconfiguredAreas.length > 0;
  const hasConfiguredAreasWithoutData =
    metric === "total" && configuredAreaCount > 0 && data.length === 0;
  const hasRenderableData = data.some(
    (point) => point.averageTotalPopulation != null || point.peakTotalPopulation != null
  );

  return (
    <ChartCard
      title={`${metricLabel} Trend`}
      icon={<TrendingUpIcon sx={{ fontSize: "0.875rem" }} />}
      action={
        thresholds != null && thresholds.length > 0 ? (
          <FormControlLabel
            label="Show thresholds"
            control={
              <Checkbox
                size="small"
                checked={showThresholds}
                onChange={(event) => setShowThresholds(event.target.checked)}
                slotProps={{ input: { "aria-label": "Show thresholds" } }}
              />
            }
            sx={{
              mr: 0,
              color: "text.secondary",
              "& .MuiFormControlLabel-label": { fontSize: 12 },
            }}
          />
        ) : undefined
      }
      footerNote={`Tracks average and peak ${metric === "duty-free" ? "Duty Free" : "total"} population over time.`}
    >
      {waitingForFilters ? (
        <ChartEmptyState
          title="Select playback filters"
          description="Choose an area and date range above, then select Apply to load playback data."
        />
      ) : loading ? (
        <ChartLoading label={`Loading ${metricLabel} trend data`} />
      ) : errorMessage != null ? (
        <ChartErrorState message={errorMessage} />
      ) : isAllUnconfigured ? (
        <ChartEmptyState
          title={`${metricLabel} is not configured`}
          description={
            selectedAreaCount === 1
              ? `${metricLabel} is not configured for this area.`
              : `${metricLabel} is not configured for the selected areas (${excludedAreaNames}).`
          }
        />
      ) : hasConfiguredAreasWithoutData ? (
        <ChartEmptyState
          title={`No ${metricLabel} trend data`}
          description={`No ${metricLabel} data is available for the selected period for ${configuredAreaCount} configured ${configuredAreaCount === 1 ? "area" : "areas"}.${
            unconfiguredAreas.length > 0
              ? ` ${unconfiguredAreas.length} ${unconfiguredAreas.length === 1 ? "area is" : "areas are"} excluded because ${metricLabel} is not configured.`
              : ""
          }`}
        />
      ) : data.length === 0 || !hasRenderableData ? (
        <ChartEmptyState
          title={`No ${metricLabel} trend data`}
          description={`${metricLabel} trend data will appear once observations are available.`}
        />
      ) : (
        <>
          {unconfiguredAreas.length > 0 && (
            <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
              Showing Total Population for {configuredAreaCount} of {selectedAreaCount} areas.{" "}
              {unconfiguredAreas.length} area
              {unconfiguredAreas.length === 1 ? " is" : "s are"} not configured ({excludedAreaNames}
              ).
            </Typography>
          )}
          <MultiLineChart
            data={chartData}
            config={chartConfig}
            height="100%"
            activeTimestamp={playbackTimestamp}
            additionalPins={additionalPins}
            onChartReady={onChartReady}
          />
        </>
      )}
    </ChartCard>
  );
});
